/**
 * SimAnalysisPage — full page for data export + AI analysis.
 *
 * Two tabs: Data Export | Analysis & Debrief
 * Sim selector at top. Standard TTT UX.
 *
 * SPEC: MODULES/M10_INFRASTRUCTURE/SPEC_DATA_EXPORT_ANALYSIS.md
 * Pattern: KING SimAnalysisPage.tsx
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Header } from '@/components/Header'

interface SimRun {
  id: string; name: string; status: string; current_round: number; max_rounds: number; created_at: string
}

const MODELS = [
  { id: 'claude-opus-4-7', label: 'Claude Opus 4.7 (best, 1M context)' },
  { id: 'claude-opus-4-6', label: 'Claude Opus 4.6 (1M context)' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (faster, 200K)' },
]

export function SimAnalysisPage() {
  const navigate = useNavigate()
  const [simRuns, setSimRuns] = useState<SimRun[]>([])
  const [selectedSim, setSelectedSim] = useState('')
  const [tab, setTab] = useState<'export' | 'analysis'>('export')
  const [loading, setLoading] = useState(true)

  // Export state
  const [exporting, setExporting] = useState(false)
  const [exportDone, setExportDone] = useState(false)

  // Analysis state
  const [model, setModel] = useState('claude-opus-4-7')
  const [analyzing, setAnalyzing] = useState(false)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [analysisElapsed, setAnalysisElapsed] = useState(0)
  const [analysisStep, setAnalysisStep] = useState('')

  useEffect(() => {
    supabase.from('sim_runs').select('id,name,status,current_round,max_rounds,created_at')
      .neq('id', '00000000-0000-0000-0000-000000000001')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setSimRuns((data || []) as SimRun[])
        if (data?.length) setSelectedSim(data[0].id)
        setLoading(false)
      })
  }, [])

  const getToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.access_token || null
  }, [])

  const handleExport = async () => {
    if (!selectedSim) return
    setExporting(true); setExportDone(false)
    try {
      const token = await getToken()
      const resp = await fetch(`/api/sim/${selectedSim}/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!resp.ok) { alert('Export failed'); return }
      const json = await resp.json()
      const files = json.data

      // Download each file individually
      const simName = simRuns.find(s => s.id === selectedSim)?.name || selectedSim
      const date = new Date().toISOString().slice(0, 10)
      for (const [filename, content] of Object.entries(files)) {
        const isJson = filename.endsWith('.json')
        const blob = new Blob([content as string], { type: isJson ? 'application/json' : 'text/markdown;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = `${simName}_${date}_${filename}`
        a.click(); URL.revokeObjectURL(url)
        // Small delay between downloads so browser doesn't block them
        await new Promise(r => setTimeout(r, 300))
      }
      setExportDone(true)
    } catch (e) { alert(e instanceof Error ? e.message : 'Export failed') }
    finally { setExporting(false) }
  }

  const handleAnalyze = async () => {
    if (!selectedSim) return
    setAnalyzing(true); setAnalysisError(null); setAnalysisElapsed(0)
    setAnalysisStep('Assembling simulation data...')

    // Timer
    const startTime = Date.now()
    const timer = setInterval(() => setAnalysisElapsed(Math.floor((Date.now() - startTime) / 1000)), 1000)

    try {
      setTimeout(() => setAnalysisStep('Sending to AI for analysis...'), 2000)
      setTimeout(() => setAnalysisStep('AI is analyzing 6 rounds of simulation data...'), 5000)
      setTimeout(() => setAnalysisStep('Generating 12-slide debrief presentation...'), 30000)
      setTimeout(() => setAnalysisStep('Almost done — formatting visualizations...'), 60000)

      const token = await getToken()
      const resp = await fetch(`/api/sim/${selectedSim}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ model }),
      })
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}))
        setAnalysisError(j.detail || `Error ${resp.status}`)
        return
      }
      const json = await resp.json()
      setAnalysisStep('Done! Downloading presentation...')

      // Download the HTML file directly from LLM
      const simName = simRuns.find(s => s.id === selectedSim)?.name || 'TTT'
      const htmlContent = json.data.html || '<html><body><h1>Analysis generation failed</h1></body></html>'
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `${simName}_Analysis_${new Date().toISOString().slice(0, 10)}.html`
      a.click(); URL.revokeObjectURL(url)
    } catch (e) { setAnalysisError(e instanceof Error ? e.message : 'Failed') }
    finally { clearInterval(timer); setAnalyzing(false) }
  }

  const selectedSimData = simRuns.find(s => s.id === selectedSim)

  return (
    <div className="min-h-screen bg-base">
      <Header subtitle="Sim Data Analysis" />

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Back */}
        <button onClick={() => navigate('/dashboard')} className="font-body text-caption text-action hover:underline mb-6 block">
          ← Back to Dashboard
        </button>

        <h1 className="font-heading text-h1 text-text-primary mb-2">Simulation Analysis & Data Export</h1>
        <p className="font-body text-body-sm text-text-secondary mb-6">Export simulation data for external analysis or generate an AI-driven debrief presentation.</p>

        {/* Sim selector */}
        <div className="mb-6">
          <label className="font-body text-caption text-text-secondary block mb-1">Select Simulation</label>
          <select value={selectedSim} onChange={e => { setSelectedSim(e.target.value); setExportDone(false) }}
            className="w-full bg-card border border-border rounded-lg px-4 py-2.5 font-body text-body-sm text-text-primary focus:outline-none focus:border-action"
            disabled={loading}>
            {loading && <option>Loading...</option>}
            {simRuns.map(s => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.status}) — R{s.current_round}/{s.max_rounds} — {s.created_at.slice(0, 10)}
              </option>
            ))}
          </select>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border mb-6">
          <button onClick={() => setTab('export')}
            className={`font-body text-body-sm px-5 py-2.5 transition-colors ${tab === 'export' ? 'text-action font-medium border-b-2 border-action' : 'text-text-secondary hover:text-text-primary'}`}>
            Data Export
          </button>
          <button onClick={() => setTab('analysis')}
            className={`font-body text-body-sm px-5 py-2.5 transition-colors ${tab === 'analysis' ? 'text-action font-medium border-b-2 border-action' : 'text-text-secondary hover:text-text-primary'}`}>
            Analysis & Debrief
          </button>
        </div>

        {/* ═══ TAB 1: DATA EXPORT ═══ */}
        {tab === 'export' && (
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="font-heading text-h3 text-text-primary mb-3">Export Simulation Data</h2>
              <p className="font-body text-body-sm text-text-secondary mb-4">
                Downloads a JSON bundle containing 6 structured files — ready to feed to any AI system for analysis.
              </p>

              <div className="bg-base rounded-lg p-4 mb-4">
                <h3 className="font-body text-caption text-text-secondary uppercase tracking-wider mb-2">Files included</h3>
                <div className="space-y-1 font-body text-body-sm text-text-primary">
                  <div>1. <strong>World Context</strong> — Thucydides Trap concept, countries, roles, initial state, rules</div>
                  <div>2. <strong>Events Timeline</strong> — every action and outcome, chronologically</div>
                  <div>3. <strong>State Evolution</strong> — per-round country snapshots, relationships, agreements</div>
                  <div>4. <strong>Conversations</strong> — meeting transcripts, Navigator conversations, public statements</div>
                  <div>5. <strong>Actions by Country</strong> — complete action log per country</div>
                  <div>6. <strong>Summary (JSON)</strong> — final rankings, wars, key metrics</div>
                </div>
              </div>

              {selectedSimData && (
                <div className="flex items-center justify-between">
                  <span className="font-body text-caption text-text-secondary">
                    Selected: <strong className="text-text-primary">{selectedSimData.name}</strong> · R{selectedSimData.current_round}/{selectedSimData.max_rounds} · {selectedSimData.status}
                  </span>
                  <button onClick={handleExport} disabled={exporting || !selectedSim}
                    className="bg-action text-white font-body text-body-sm font-medium px-5 py-2 rounded hover:opacity-90 disabled:opacity-50 transition-opacity">
                    {exporting ? 'Exporting...' : 'Download Data Pack'}
                  </button>
                </div>
              )}

              {exportDone && (
                <div className="mt-3 bg-success/10 border border-success/30 rounded-lg px-4 py-2">
                  <p className="font-body text-caption text-success font-medium">Export downloaded successfully. Open with Claude Code or any AI assistant using POSTSIM_DATA_ANALYSIS.md as the analysis prompt.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ TAB 2: ANALYSIS & DEBRIEF ═══ */}
        {tab === 'analysis' && (
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="font-heading text-h3 text-text-primary mb-3">Generate AI Debrief</h2>
              <p className="font-body text-body-sm text-text-secondary mb-4">
                Generates a 12-slide professional debrief presentation analyzing the simulation outcomes, turning points, alliance dynamics, and role performance.
              </p>

              {/* Model selector */}
              <div className="mb-4">
                <label className="font-body text-caption text-text-secondary block mb-1">AI Model</label>
                <select value={model} onChange={e => setModel(e.target.value)}
                  className="w-full bg-base border border-border rounded-lg px-4 py-2.5 font-body text-body-sm text-text-primary focus:outline-none focus:border-action">
                  {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>

              {/* What the analysis covers */}
              <div className="bg-base rounded-lg p-4 mb-4">
                <h3 className="font-body text-caption text-text-secondary uppercase tracking-wider mb-2">Analysis covers</h3>
                <div className="grid grid-cols-2 gap-1 font-body text-caption text-text-primary">
                  <div>• Future scenarios & probabilities</div>
                  <div>• Critical turning points</div>
                  <div>• Columbia vs Cathay dynamics</div>
                  <div>• Wars & conflict outcomes</div>
                  <div>• Alliance formation & shifts</div>
                  <div>• Regional leadership questions</div>
                  <div>• Technology & economy trends</div>
                  <div>• 20 countries final assessment</div>
                  <div>• Role performance scores (1-10)</div>
                  <div>• Thucydides Trap mechanism check</div>
                  <div>• Lessons & real-world parallels</div>
                </div>
              </div>

              {selectedSimData && (
                <div className="flex items-center justify-between">
                  <span className="font-body text-caption text-text-secondary">
                    Selected: <strong className="text-text-primary">{selectedSimData.name}</strong> · Est. cost: ~$3-5
                  </span>
                  <button onClick={handleAnalyze} disabled={analyzing || !selectedSim}
                    className="bg-action text-white font-body text-body-sm font-medium px-5 py-2 rounded hover:opacity-90 disabled:opacity-50 transition-opacity">
                    {analyzing ? 'Generating...' : 'Generate Analysis'}
                  </button>
                </div>
              )}

              {analyzing && (
                <div className="mt-4 bg-action/5 border border-action/20 rounded-lg px-5 py-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-4 h-4 border-2 border-action border-t-transparent rounded-full animate-spin" />
                    <span className="font-body text-body-sm text-text-primary font-medium">{analysisStep}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-caption text-text-secondary">Elapsed: {Math.floor(analysisElapsed / 60)}:{(analysisElapsed % 60).toString().padStart(2, '0')}</span>
                    <span className="font-body text-caption text-text-secondary">Model: {model}</span>
                  </div>
                </div>
              )}

              {analysisError && (
                <div className="mt-3 bg-danger/10 border border-danger/30 rounded-lg px-4 py-2">
                  <p className="font-body text-caption text-danger">{analysisError}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

/** Build a self-contained HTML presentation file from slide data */
function buildAnalysisHTML(simName: string, slides: Record<string, unknown>[], meta: Record<string, unknown>): string {
  const slideHTML = slides.map((s: Record<string, unknown>, i: number) => {
    const dataPoints = (s.data_points as {label:string;value:string}[] || [])
      .map(dp => `<div><div style="font-size:9pt;color:#888;text-transform:uppercase;letter-spacing:0.1em">${dp.label}</div><div style="font-family:'JetBrains Mono',monospace;font-size:16pt;color:#1a1a2e;font-weight:700">${dp.value}</div></div>`)
      .join('')

    return `
    <div class="slide" id="slide-${i}" style="display:${i === 0 ? 'flex' : 'none'}">
      <div class="slide-inner">
        <h1>${s.title || ''}</h1>
        ${s.subtitle ? `<p class="subtitle">${s.subtitle}</p>` : ''}
        ${dataPoints ? `<div class="data-row">${dataPoints}</div>` : ''}
        <div class="content">${String(s.content || '').replace(/\n/g, '<br/>')}</div>
      </div>
    </div>`
  }).join('\n')

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${simName} — Post-Simulation Analysis</title>
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: 'DM Sans', sans-serif; background: #FAFAF8; color: #333; }
.nav { position: fixed; top: 0; left: 0; right: 0; z-index: 50; background: #fff; border-bottom: 1px solid #eee; padding: 8px 24px; display: flex; align-items: center; justify-content: space-between; }
.nav-title { font-family: 'Playfair Display', serif; font-size: 13pt; color: #1a1a2e; }
.nav-info { font-size: 10pt; color: #888; margin-left: 12px; }
.nav-btn { padding: 4px 12px; border: 1px solid #ddd; border-radius: 4px; background: #fff; cursor: pointer; font-size: 11pt; }
.nav-btn:disabled { opacity: 0.3; }
.slide { min-height: 100vh; padding-top: 56px; display: flex; align-items: center; justify-content: center; }
.slide-inner { max-width: 900px; width: 100%; padding: 40px 48px; background: #fff; border-radius: 12px; box-shadow: 0 2px 20px rgba(0,0,0,0.06); margin: 24px auto; min-height: 500px; }
h1 { font-family: 'Playfair Display', serif; font-size: 22pt; color: #1a1a2e; margin-bottom: 8px; }
.subtitle { font-size: 12pt; color: #666; margin-bottom: 16px; }
.data-row { display: flex; gap: 24px; margin-bottom: 20px; flex-wrap: wrap; }
.content { font-size: 11pt; line-height: 1.6; color: #333; }
.dots { position: fixed; bottom: 16px; left: 0; right: 0; display: flex; justify-content: center; gap: 6px; }
.dot { width: 8px; height: 8px; border-radius: 4px; border: none; background: #ddd; cursor: pointer; transition: all 0.2s; }
.dot.active { width: 20px; background: #3B82F6; }
.meta { font-size: 9pt; color: #aaa; }
@media print { .nav, .dots { display: none; } .slide { display: flex !important; page-break-after: always; } }
</style>
</head>
<body>
<div class="nav">
  <div style="display:flex;align-items:center;gap:12px">
    <span class="nav-title">Thucydides Trap — Analysis</span>
    <span class="nav-info" id="slideCounter">Slide 1 / ${slides.length}</span>
  </div>
  <div style="display:flex;align-items:center;gap:8px">
    <button class="nav-btn" onclick="prev()" id="prevBtn">←</button>
    <button class="nav-btn" onclick="next()" id="nextBtn">→</button>
    <button class="nav-btn" onclick="window.print()">Print</button>
    <span class="meta">${meta.model || ''} · ${(meta.input_tokens as number || 0).toLocaleString()}→${(meta.output_tokens as number || 0).toLocaleString()} tokens · $${((meta.cost_usd as number) || 0).toFixed(2)}</span>
  </div>
</div>

${slideHTML}

<div class="dots" id="dots">
${slides.map((_: unknown, i: number) => `<button class="dot${i === 0 ? ' active' : ''}" onclick="goTo(${i})"></button>`).join('')}
</div>

<script>
let current = 0;
const total = ${slides.length};
function show(n) {
  document.querySelectorAll('.slide').forEach((s,i) => s.style.display = i === n ? 'flex' : 'none');
  document.querySelectorAll('.dot').forEach((d,i) => d.className = 'dot' + (i === n ? ' active' : ''));
  document.getElementById('slideCounter').textContent = 'Slide ' + (n+1) + ' / ' + total;
  document.getElementById('prevBtn').disabled = n === 0;
  document.getElementById('nextBtn').disabled = n === total - 1;
  current = n;
}
function next() { if (current < total-1) show(current+1); }
function prev() { if (current > 0) show(current-1); }
function goTo(n) { show(n); }
document.addEventListener('keydown', e => { if (e.key==='ArrowRight'||e.key===' ') next(); if (e.key==='ArrowLeft') prev(); });
document.querySelector('.slide-inner')?.addEventListener('click', next);
</script>
</body>
</html>`
}

